import { dayjs } from "@thalamos/common";
import { DolsTrackingStatus, UnassessedStatuses, } from "../types/capacityState/CapacityState.js";
import { capacityDeadlines, capacityStatusOf } from "./capacityStatusMatrix.js";
/**
 * Warning thresholds per clock. The two differ by an order of magnitude because the
 * authorisations do: an urgent authorisation lasts 7 days, so a day's notice matters,
 * while a standard authorisation runs to an annual review where weeks is the useful
 * unit. Mirrors expiryThresholdTimes for MHA statuses.
 */
export const capacityExpiryThresholds = {
    "urgent-authorisation": {
        warning: { unit: "days", value: 4 },
        critical: { unit: "days", value: 2 },
    },
    "standard-authorisation": {
        warning: { unit: "weeks", value: 8 },
        critical: { unit: "weeks", value: 2 },
    },
    "referral-age": {
        warning: { unit: "days", value: 7 },
        critical: { unit: "days", value: 21 },
    },
    // A review falls due rather than expiring, so it is flagged on the same horizon as an
    // authorisation review: long enough to arrange an assessment, not so long that the
    // register cries out about something a season away.
    "capacity-review": {
        warning: { unit: "weeks", value: 8 },
        critical: { unit: "weeks", value: 2 },
    },
};
/**
 * How urgently a state needs attention, taking whichever of its clocks is nearest.
 * A resident can be counting down two at once, so the worst one wins.
 */
export function classifyCapacityExpiry(state, now, reviewIntervalMonths) {
    // A lapsed standard authorisation means the deprivation is currently
    // unauthorised, whatever the dates now say.
    if (state.dols?.status === DolsTrackingStatus.StandardAuthorisationExpired) {
        return "expired";
    }
    // Nothing has been sent to the local authority. There is no expiry date to read,
    // so the clock runs on how long it has been sitting.
    if (state.dols?.status === DolsTrackingStatus.ReferralNotStarted) {
        const since = dayjs(state.dols.startDateTime ?? now.toISOString());
        const thresholds = capacityExpiryThresholds["referral-age"];
        const age = now.diff(since, "days");
        if (age >= thresholds.critical.value)
            return "critical";
        if (age >= thresholds.warning.value)
            return "warning";
        return "normal";
    }
    const deadlines = capacityDeadlines(state, reviewIntervalMonths);
    if (deadlines.length === 0) {
        return "none";
    }
    const classifications = deadlines.map((deadline) => {
        const at = dayjs(deadline.dateTime);
        if (at.isBefore(now))
            return "expired";
        const thresholds = capacityExpiryThresholds[deadline.kind];
        if (at.isBefore(now.add(thresholds.critical.value, thresholds.critical.unit))) {
            return "critical";
        }
        if (at.isBefore(now.add(thresholds.warning.value, thresholds.warning.unit))) {
            return "warning";
        }
        return "normal";
    });
    const order = [
        "expired",
        "critical",
        "warning",
        "normal",
    ];
    return (order.find((level) => classifications.includes(level)) ?? "none");
}
/**
 * A register cannot be emptied, so standing is expressed as how much of it is
 * current rather than how little is left. `statusCurrent` deliberately counts only
 * residents whose position is both assessed and not lapsing — it is the number a
 * governance lead is asked for.
 */
export function summariseRegisterCoverage(states, now, reviewIntervalMonths) {
    let statusCurrent = 0;
    let dueSoon = 0;
    let outOfDate = 0;
    let noReviewDate = 0;
    let unassessed = 0;
    for (const state of states) {
        if (UnassessedStatuses.includes(capacityStatusOf(state))) {
            unassessed += 1;
            continue;
        }
        const classification = classifyCapacityExpiry(state, now, reviewIntervalMonths);
        switch (classification) {
            case "expired":
                outOfDate += 1;
                break;
            case "critical":
            case "warning":
                dueSoon += 1;
                break;
            // Assessed, but with no authorisation running and no review policy to measure
            // against. Counting these as current would assert a currency nothing establishes:
            // an assessment from three years ago would read as up to date for ever.
            case "none":
                noReviewDate += 1;
                break;
            default:
                statusCurrent += 1;
        }
    }
    return {
        residents: states.length,
        statusCurrent,
        dueSoon,
        outOfDate,
        noReviewDate,
        unassessed,
    };
}
//# sourceMappingURL=capacityExpiry.js.map